(() => {
  'use strict';

  const SELECTOR = '[data-mf-msi-hero-v3]';
  const COMPETITIVE_STATE_KEY = 'mf_competitive:current';
  const COMPETITIVE_EVENT = 'mf:competitive-data-ready';
  const LOCAL_STATE_MAX_AGE = 24 * 60 * 60 * 1000;

  const qs = (selector, scope = document) =>
    scope.querySelector(selector);

  const toNumber = (value) => {
    const number = Number(value);
    return Number.isFinite(number)
      ? number
      : 0;
  };

  const setText = (
    root,
    selector,
    value
  ) => {
    const element =
      qs(selector, root);

    if (element) {
      element.textContent =
        value;
    }
  };

  const readSharedState = () => {
    const memoryState =
      window.__MF_COMPETITIVE_STATE__;

    if (
      memoryState &&
      memoryState.season
    ) {
      return memoryState;
    }

    try {
      const raw =
        window.localStorage.getItem(
          COMPETITIVE_STATE_KEY
        );

      if (!raw) {
        return null;
      }

      const state =
        JSON.parse(raw);

      const age =
        Date.now() -
        toNumber(state?.savedAt);

      if (
        !state ||
        !state.season ||
        !Number.isFinite(age) ||
        age < 0 ||
        age > LOCAL_STATE_MAX_AGE
      ) {
        return null;
      }

      return state;
    } catch (error) {
      return null;
    }
  };

  const render = (
    root,
    model,
    source = 'shared'
  ) => {
    if (!model?.season) {
      return;
    }

    const season =
      model.season;

    const leader =
      model.leader || {
        name: 'Por definir',
        points: 0
      };

    const current =
      toNumber(
        season.current
      );

    const total =
      Math.max(
        1,
        toNumber(
          season.total
        ) || 18
      );

    const progress =
      Math.max(
        0,
        Math.min(
          100,
          toNumber(
            season.progress
          )
        )
      );

    const seasonName =
      String(
        season.name ||
        season.id ||
        'Temporada'
      );

    const status =
      String(
        season.status || ''
      )
        .trim()
        .toUpperCase();

    setText(
      root,
      '[data-mf-season-name]',
      seasonName
    );

    setText(
      root,
      '[data-mf-tournaments-current]',
      current
    );

    setText(
      root,
      '[data-mf-tournaments-total]',
      total
    );

    setText(
      root,
      '[data-mf-player-count]',
      toNumber(
        model.playerCount
      )
    );

    setText(
      root,
      '[data-mf-leader-name]',
      leader.name ||
      'Por definir'
    );

    setText(
      root,
      '[data-mf-leader-points]',
      toNumber(
        leader.points
      )
    );

    setText(
      root,
      '[data-mf-progress-copy]',
      `${current} de ${total} torneos`
    );

    setText(
      root,
      '[data-mf-progress-value]',
      `${progress}%`
    );

    const closed =
      status === 'CERRADA' ||
      status === 'CERRADO' ||
      status === 'HISTORICO' ||
      status === 'HISTÓRICO';

    setText(
      root,
      '[data-mf-live-status-text]',
      closed
        ? 'Temporada finalizada'
        : 'Temporada en curso'
    );

    const live =
      qs(
        '[data-mf-live-status]',
        root
      );

    if (live) {
      live.classList.toggle(
        'is-closed',
        closed
      );
    }

    const fill =
      qs(
        '[data-mf-progress-fill]',
        root
      );

    if (fill) {
      window.requestAnimationFrame(
        () => {
          fill.style.width =
            `${progress}%`;
        }
      );
    }

    const track =
      qs(
        '[data-mf-progress-track]',
        root
      );

    if (track) {
      track.setAttribute(
        'aria-valuenow',
        String(progress)
      );
    }

    setText(
      root,
      '[data-mf-sync-state]',
      source === 'event'
        ? 'Actualizado'
        : 'Datos guardados'
    );

    root.classList.add(
      'is-hydrated'
    );
  };

  const init = (root) => {
    if (
      !root ||
      root.dataset
        .mfMsiHeroV4Initialized ===
        'true'
    ) {
      return;
    }

    root.dataset
      .mfMsiHeroV4Initialized =
      'true';

    const cached =
      readSharedState();

    if (cached) {
      render(
        root,
        cached,
        'cache'
      );
    } else {
      setText(
        root,
        '[data-mf-sync-state]',
        'Esperando ranking'
      );
    }

    const handler = (event) => {
      const model =
        event?.detail;

      if (!model) {
        return;
      }

      render(
        root,
        model,
        'event'
      );
    };

    root._mfCompetitiveHandler =
      handler;

    document.addEventListener(
      COMPETITIVE_EVENT,
      handler
    );
  };

  const destroy = (root) => {
    if (
      root?._mfCompetitiveHandler
    ) {
      document.removeEventListener(
        COMPETITIVE_EVENT,
        root._mfCompetitiveHandler
      );

      root._mfCompetitiveHandler =
        null;
    }

    if (root?.dataset) {
      root.dataset
        .mfMsiHeroV4Initialized =
        'false';
    }
  };

  const boot = () => {
    document
      .querySelectorAll(
        SELECTOR
      )
      .forEach(init);
  };

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      boot,
      { once: true }
    );
  } else {
    boot();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      const scope =
        event.target;

      if (!scope) {
        return;
      }

      if (
        scope.matches?.(
          SELECTOR
        )
      ) {
        destroy(scope);
        init(scope);
        return;
      }

      const root =
        scope.querySelector?.(
          SELECTOR
        );

      if (root) {
        destroy(root);
        init(root);
      }
    }
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      const scope =
        event.target;

      if (!scope) {
        return;
      }

      if (
        scope.matches?.(
          SELECTOR
        )
      ) {
        destroy(scope);
        return;
      }

      const root =
        scope.querySelector?.(
          SELECTOR
        );

      if (root) {
        destroy(root);
      }
    }
  );
})();